// An in-page tab strip whose tabs are ROUTES, not local state.
//
// Two sections use it — Talent Management (Dashboard / Searches) and Alerts
// (Worklist / Big clients) — and both want the same thing from it: a lane you can
// link someone to, that survives a refresh, and that does not refetch the other
// lane's query when you switch. `activeProps` gives the current-page styling for
// free, so there is no active-tab state to hold.
//
// `to` is typed as the router's own link target, so a tab pointing at a path that
// does not exist fails typecheck rather than rendering a dead link.
import { Link, type LinkProps } from "@tanstack/react-router";

import "./section-tabs.css";

export interface SectionTab {
  label: string;
  to: LinkProps["to"];
  /** Match this path EXACTLY. Needed on a tab whose path is a prefix of its
   *  siblings' (`/alerts` under `/alerts/big-clients`), which would otherwise
   *  render as active on both. */
  exact?: boolean;
}

/** Talent Management's lanes: the BI dashboard and the operational lists. */
export const TALENT_TABS: readonly SectionTab[] = [
  { label: "Dashboard", to: "/talent-management", exact: true },
  { label: "Searches", to: "/talent-management/searches" },
  { label: "Offers", to: "/talent-management/offers" },
  { label: "Declines", to: "/talent-management/declines" },
  { label: "VIP sellers", to: "/talent-management/vip" },
  { label: "Excluded", to: "/talent-management/excluded" },
  // Two tabs, two objects, deliberately adjacent: "Opt-outs" is the suppression
  // list itself (ids excluded from every search), "Opt-out requests" is the queue
  // of talents who ASKED, which is what feeds it. Naming them alike is the point,
  // since the second is where you go to fill the first.
  { label: "Opt-outs", to: "/talent-management/optout" },
  { label: "Opt-out requests", to: "/talent-management/optout-requests" },
  { label: "Preferences", to: "/talent-management/preferences" },
];

/** Alerts' two lanes: every raised flag, and the big-fish desk — the same flag
 *  store, narrowed to `big_client` and given the controls that desk acts with. */
export const ALERTS_TABS: readonly SectionTab[] = [
  { label: "Worklist", to: "/alerts", exact: true },
  { label: "Big clients", to: "/alerts/big-clients" },
];

export function SectionTabs({ label, tabs }: { label: string; tabs: readonly SectionTab[] }) {
  return (
    <nav className="st-tabs" aria-label={label}>
      {tabs.map((tab) => (
        <Link
          key={tab.label}
          to={tab.to}
          className="st-tab"
          activeProps={{ className: "st-tab st-tab--active", "aria-current": "page" }}
          activeOptions={tab.exact ? { exact: true } : undefined}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
